import { Box, Typography } from '@mui/material';
import { IconCustomSparkles } from 'components/icons/components/custom/IconCustomSparkles';
import { WordLimitedInputArea } from 'features/socialMediaListening/components';
import { useState } from 'react';
import { theme } from 'styles/theme';
import { RenderNextButton } from '../renderNextButton/RenderNextButton';

type SocialMediaListeningOnboardingPostPriorityInfoProps = {
  value: string;
  setValue: (value: string) => void;
  onNext: () => void;
  type?: 'creator' | 'topic';
  componentProps?: {
    title?: string;
    subtitle?: string;
    inputPlaceholder?: string;
  };
};

export const SocialMediaListeningOnboardingPostPriorityInfo = ({
  value,
  setValue,
  onNext,
  type,
  componentProps,
}: SocialMediaListeningOnboardingPostPriorityInfoProps) => {
  const [hasError, setHasError] = useState(false);
  const validateAndProceed = () => {
    if (!value.trim().length) {
      setHasError(true);
    } else {
      setHasError(false);
      onNext();
    }
  };

  return (
    <Box
      width="60vw"
      maxHeight="65vh"
      display="flex"
      gap={4}
      flexDirection="column"
      color={theme.colors?.primary.parchment}
    >
      {type && (
        <Typography
          variant="body-lg"
          sx={{
            fontWeight: 600,
            width: 'fit-content',
            padding: theme.spacing(1, 3),
            bgcolor: 'rgba(250, 243, 236, 0.20)',
            borderRadius: theme.spacing(4),
          }}
        >
          {type === 'topic' && 'Topic Insights'}
          {type === 'creator' && 'Creator Focus'}
        </Typography>
      )}
      <Typography
        variant="headline-lg"
        fontSize={theme.spacing(7)}
        fontWeight={600}
      >
        {componentProps?.title ||
          'How should we prioritize incoming posts for you?'}
      </Typography>
      <Typography variant="body-xl" fontWeight={500}>
        {componentProps?.subtitle ||
          'When there are a lot of relevant contents, how should we prioritize them for engagement purposes?'}
      </Typography>

      <WordLimitedInputArea
        maxWords={100}
        componentProps={{
          wordUsage: {
            sx: {
              color: theme.colors?.utility[500],
            },
          },
        }}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={async (e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            validateAndProceed();
          }
        }}
        autoFocus
        placeholder={
          componentProps?.inputPlaceholder ||
          `For example:
Prioritize posts from creators in the same industry as us, and have more than 20k followers.
Prioiritize posts with strong positive or strong negative sentiments`
        }
        multiline
        minRows={10}
        maxRows={16}
        sx={{
          mt: 4,
          '.MuiOutlinedInput-root': {
            ...(hasError
              ? { border: `2px solid ${theme.colors?.utility['pink-3']}` }
              : {}),
            borderRadius: theme.spacing(4),
            py: `${theme.spacing(8)} !important`,
            px: `${theme.spacing(8)} !important`,
            bgcolor: 'rgba(255, 255, 255, 0.10)',
            color: theme.colors?.primary.parchment,

            input: {
              p: '0 !important',
              ...theme.typography['body-xl'],
            },

            '.MuiOutlinedInput-notchedOutline': {
              display: 'none !important',
            },
          },
        }}
      />

      <RenderNextButton onNextClick={validateAndProceed} />
    </Box>
  );
};
